import type { Session } from '@ory/client';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Role } from 'types/roles';

import { useAuth } from '../../hooks';

export enum legalType {
	NATURAL_PERSON = 'NATURAL_PERSON',
	LEGAL_ENTITY = 'LEGAL_ENTITY',
}

export enum LegalDocumentType {
	TERMS_AND_CONDITIONS = 'terms_and_conditions',
	PRIVACY_POLICY = 'privacy_policy',
}

export const LEGAL_DOCUMENT_TYPES = Object.values(LegalDocumentType);

export interface PendingLegalDocument {
	uuid: string;
	type: LegalDocumentType;
}

export interface Profile {
	id: string;
	name: string;
	secondFactorMethod: 'sms' | 'email';
	customers: Customer[];
	pending_legal_documents?: PendingLegalDocument[];
}

export enum CustomerStatus {
	OPEN = 'OPEN',
	PENDING = 'PENDING',
	CLOSED = 'CLOSED',
	BLOCKED = 'BLOCKED',
	ABANDONED = 'ABANDONED',
	DORMANT = 'DORMANT',
}

export interface Customer {
	id: string;
	legalType: legalType;
	name: string;
	role: Role;
	status: CustomerStatus;
	odd_eligible: boolean;
}

export const useProfiles = () => {
	const { session } = useAuth();

	const fetchLatestOperations = async (
		passedSession: Session | undefined,
	): Promise<Profile> => {
		const response = await axios.get(`${import.meta.env.VITE_API_HOST}/api/v1/profiles`, {
			headers: { Authorization: passedSession?.tokenized },
		});

		return response.data;
	};

	return useQuery({
		enabled: !!session?.tokenized,
		staleTime: 1000 * 60 * 60 * 2,
		queryKey: ['profiles', session?.identity?.id],
		queryFn: () => fetchLatestOperations(session),
		select: (profile) => {
			const knownRoles = Object.values(Role);
			profile.customers.forEach((customer) => {
				if (!knownRoles.includes(customer.role)) {
					console.warn(
						`Unknown profile role "${customer.role}" for customer ${customer.id}; capabilities will be denied.`,
					);
				}
			});
			return profile;
		},
	});
};
